import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from "@/contexts/AuthContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { CookieManager } from "@/components/CookieManager";
import { lazy, Suspense } from "react";
import { useCSP } from "@/hooks/useCSP";
import Index from "./pages/Index";

// Lazy load non-critical pages
const Admin = lazy(() => import("./pages/Admin"));
const About = lazy(() => import("./pages/About"));
const Archive = lazy(() => import("./pages/Archive"));
const Products = lazy(() => import("./pages/Products"));
const TipDetail = lazy(() => import("./pages/TipDetail"));
const TechTipDetail = lazy(() => import("./pages/TechTipDetail"));
const TechTips = lazy(() => import("./pages/TechTips"));
const Search = lazy(() => import("./pages/Search"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Auth = lazy(() => import("./pages/Auth"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Experiences = lazy(() => import("./pages/Experiences"));
const SitemapXML = lazy(() => import("./pages/SitemapXML"));
const InvestmentTipDetail = lazy(() => import("./pages/InvestmentTipDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  // Temporarily disable CSP until properly configured
  // useCSP();
  
  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <CookieConsentProvider>
        <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/search" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><Search /></Suspense>} />
            <Route path="/favorites" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><Favorites /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><Admin /></Suspense>} />
            <Route path="/about" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><About /></Suspense>} />
             <Route path="/archive" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><Archive /></Suspense>} />
             <Route path="/products" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><Products /></Suspense>} />
             <Route path="/tips/:id" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><TipDetail /></Suspense>} />
             <Route path="/tech-tips" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><TechTips /></Suspense>} />
             <Route path="/tech-tips/:id" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><TechTipDetail /></Suspense>} />
             <Route path="/investment-tips/:id" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><InvestmentTipDetail /></Suspense>} />
            <Route path="/auth" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><Auth /></Suspense>} />
            <Route path="/unsubscribe" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><Unsubscribe /></Suspense>} />
            <Route path="/experiences" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><Experiences /></Suspense>} />
            <Route path="/sitemap.xml" element={<Suspense fallback={null}><SitemapXML /></Suspense>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading...</div>}><NotFound /></Suspense>} />
          </Routes>
          <CookieManager />
        </BrowserRouter>
      </TooltipProvider>
      </AuthProvider>
      </CookieConsentProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
